// frontend/src/modules/payroll/pages/PayslipDetailPage.tsx
//
// Per-payslip detail view — `/dashboard/payroll/payslips/:id`.
//
// Everything renders from the single enriched GET /api/payroll/payslips/:id
// response (payslip + run + ytd + components + employee summary). No extra
// fetches: employees hold only payroll.view_self, so the old follow-up calls
// to /runs/:id/components (payroll.view) and /api/hr/employees (HR-gated)
// 403'd for them, leaving an empty payslip with a UUID title.
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, Banknote, FileDown, Mail, ExternalLink } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { payrollApi, type PayslipDetail } from '../api';
import { isRunCommitted } from '../runStatus';
import { PayslipPdfPreview } from '../components/PayslipPdfPreview';
import type { Payslip, PayrollRun, PayrollComponent } from '../types';

const ADDITION_TYPES = new Set(['base', 'overtime', 'allowance', 'bonus']);

export function PayslipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';

  // Single fetch: payslip + run + ytd + components + employee summary.
  const [resolved, setResolved] = React.useState<PayslipDetail | null>(null);
  const [resolving, setResolving] = React.useState(true);
  const [resolveError, setResolveError] = React.useState<string | null>(null);
  const [emailing, setEmailing] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!id) return;
    setResolving(true);
    setResolveError(null);
    payrollApi.findPayslip(id)
      .then((hit) => {
        if (cancelled) return;
        if (!hit) setResolveError('not_found');
        else setResolved(hit);
      })
      .catch((err) => { if (!cancelled) setResolveError(err?.message ?? 'unknown'); })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (resolving) return <LoadingState variant="page" />;
  if (resolveError || !resolved) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 pt-16 pb-12 max-w-2xl">
        <div className={[VISUAL.bento, 'p-10 text-center'].join(' ')}>
          <p className="text-[16px] font-semibold text-foreground">Payslip not found</p>
          <p className="text-[13px] text-muted-foreground mt-2">
            The payslip may have been removed, or you don't have permission to view it.
          </p>
          <div className="mt-5">
            <Link to="/dashboard/payroll/payslips">
              <Button variant="outline" className="rounded-full gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to payslips
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { payslip, run, ytd } = resolved;
  const components = resolved.components ?? [];
  const employee = resolved.employee ?? null;

  const earnings = components.filter((c) => ADDITION_TYPES.has(c.component_type));
  const deductions = components.filter((c) => !ADDITION_TYPES.has(c.component_type));

  const gross = Number(payslip.gross_additions);
  const totalDeductions = Number(payslip.gross_deductions);
  const net = Number(payslip.net_amount);

  // Status tone — mirrors run + delivery state.
  const { tone, label } = payslipStatus(payslip, run);
  const statusChip = (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {label}
    </span>
  );

  const fullName = resolved.employee_name ?? employee?.name ?? payslip.employee_id.slice(0, 8);

  function downloadPdf() {
    payrollApi.downloadPayslipPdf(payslip.id).catch((err: any) =>
      toast.error('Download failed', { description: err?.message ?? String(err) }),
    );
  }

  async function emailPayslip() {
    if (!isRunCommitted(run)) {
      toast.error('Finalize the run first', {
        description: 'Payslips can only be emailed once the parent run is finalized.',
      });
      return;
    }
    if (emailing) return;
    setEmailing(true);
    try {
      const result = await payrollApi.emailPayslip(payslip.id);
      if (result.sent) {
        toast.success(payslip.delivered_at ? 'Resent payslip' : 'Payslip emailed', {
          description: 'Delivered as PDF attachment.',
        });
        // Refresh so delivered_at flips in the UI.
        const refreshed = await payrollApi.findPayslip(payslip.id);
        if (refreshed) setResolved(refreshed);
      } else if (result.reason === 'no_employee_email') {
        toast.error('Cannot email payslip', { description: 'Employee has no email address on file.' });
      } else {
        toast.error('Email failed', { description: result.reason ?? 'Unknown reason' });
      }
    } catch (err: any) {
      toast.error('Email failed', { description: err.message ?? String(err) });
    } finally {
      setEmailing(false);
    }
  }

  return (
    <div className="flex h-full min-h-screen">
      <div className="flex-1 min-w-0 overflow-auto">
        <PageHeader
          eyebrow={
            <>
              <Link to="/dashboard/payroll/payslips" className="hover:text-foreground transition-colors">Payslips</Link>
              {' / '}
              <Link to={`/dashboard/payroll/runs/${run.id}`} className="hover:text-foreground transition-colors">
                {formatDate(run.period_start)} – {formatDate(run.period_end)}
              </Link>
            </>
          }
          title={fullName}
          chip={statusChip}
          description={
            <span>
              Payslip for the pay period {formatDate(run.period_start)} – {formatDate(run.period_end)}
              {employee?.designation ? ` · ${employee.designation}` : ''}
              {employee?.employee_no ? ` · #${employee.employee_no}` : ''}
            </span>
          }
          actions={
            <div className="flex items-center gap-2">
              <Link to="/dashboard/payroll/payslips">
                <Button variant="ghost" className="rounded-full h-10 px-3 text-[13px] font-semibold gap-1.5 text-muted-foreground">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </Button>
              </Link>
              <Button
                type="button"
                variant="outline"
                onClick={downloadPdf}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
              >
                <FileDown className="h-3.5 w-3.5" /> Download PDF
              </Button>
              <Button
                type="button"
                onClick={emailPayslip}
                disabled={emailing}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
              >
                <Mail className="h-3.5 w-3.5" /> {emailing ? 'Sending…' : payslip.delivered_at ? 'Resend payslip' : 'Email payslip'}
              </Button>
            </div>
          }
        />

        <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6 max-w-5xl">
          {/* Hero: employee header + net pay */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 110, damping: 22 }}
            className={[VISUAL.bento, 'p-7 grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-6 items-center'].join(' ')}
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center text-[18px] font-bold text-saffron-foreground shrink-0">
                {(fullName?.[0] ?? '?').toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className={[VISUAL.eyebrow, 'mb-1.5'].join(' ')}>Net pay</p>
                <p className={VISUAL.dashBigNumber}>{formatMoney(net, currency)}</p>
                <p className="text-[12.5px] text-muted-foreground mt-2 truncate">
                  {employee?.email ?? '—'}
                </p>
              </div>
            </div>
            <MiniKpi label="Earnings" value={formatMoney(gross, currency)} tone="success" />
            <MiniKpi label="Deductions" value={formatMoney(totalDeductions, currency)} tone="muted" />
          </motion.section>

          {/* Earnings table */}
          <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={VISUAL.dashSection}>Earnings</h3>
              <p className="text-[12px] text-muted-foreground">
                {earnings.length} line{earnings.length === 1 ? '' : 's'}
              </p>
            </div>
            {earnings.length === 0 ? (
              <p className="text-[13px] text-muted-foreground text-center py-6">
                No earnings recorded for this payslip.
              </p>
            ) : (
              <ComponentsTable rows={earnings} currency={currency} sign="+" />
            )}
          </section>

          {/* Deductions table */}
          <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={VISUAL.dashSection}>Deductions</h3>
              <p className="text-[12px] text-muted-foreground">
                {deductions.length} line{deductions.length === 1 ? '' : 's'}
              </p>
            </div>
            {deductions.length === 0 ? (
              <p className="text-[13px] text-muted-foreground text-center py-6">
                No deductions on this payslip.
              </p>
            ) : (
              <ComponentsTable rows={deductions} currency={currency} sign="−" />
            )}
          </section>

          {/* Summary — tax withholdings (if any) appear as deduction lines above. */}
          <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={VISUAL.dashSection}>Summary</h3>
            </div>
            <dl className="grid gap-1.5 text-[13.5px] max-w-md ml-auto">
              <SumRow label="Gross earnings" value={formatMoney(gross, currency)} />
              <SumRow label="Total deductions" value={`− ${formatMoney(totalDeductions, currency)}`} />
              <div className="mt-3 pt-3 border-t border-border/40 flex items-baseline justify-between gap-3">
                <span className="text-[12.5px] uppercase tracking-[0.14em] text-muted-foreground/80 font-semibold">Net pay</span>
                <span className="font-sans font-semibold text-[20px] tabular-nums tracking-[-0.02em] text-foreground">
                  {formatMoney(net, currency)}
                </span>
              </div>
              <p className="text-[11.5px] text-muted-foreground mt-3 leading-relaxed">
                Configured tax withholding rules are applied as deduction lines when the run is computed.
                Verify withholdings with your accountant before filing.
              </p>
            </dl>
          </section>

          {/* Year-to-date — finalized payslips this calendar year, incl. this one */}
          {ytd && (
            <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
              <div className="flex items-center justify-between mb-5">
                <h3 className={VISUAL.dashSection}>Year to date</h3>
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">{ytd.year}</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <YtdStat label="YTD gross" value={formatMoney(ytd.gross, currency)} />
                <YtdStat label="YTD deductions" value={formatMoney(ytd.deductions, currency)} />
                <YtdStat label="YTD net" value={formatMoney(ytd.net, currency)} emphasized />
              </div>
              {ytd.taxes.length > 0 && (
                <dl className="grid gap-1.5 text-[13px] mt-5 pt-4 border-t border-border/40 max-w-md">
                  <p className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground/80 font-semibold mb-1">
                    Tax withheld this year
                  </p>
                  {ytd.taxes.map((t) => (
                    <SumRow key={t.tax_rule_id ?? t.name} label={t.name} value={formatMoney(t.amount, currency)} />
                  ))}
                </dl>
              )}
              <p className="text-[11.5px] text-muted-foreground mt-4 leading-relaxed">
                Totals cover finalized payroll runs in {ytd.year} up to and including this payslip.
              </p>
            </section>
          )}

          {/* Run + delivery footer */}
          <section className={[VISUAL.bento, 'p-6 flex items-center gap-5'].join(' ')}>
            <div className="h-12 w-12 rounded-2xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center shrink-0">
              <Banknote className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold tracking-[-0.005em] text-foreground truncate">
                Payroll run · {formatDate(run.period_start)} – {formatDate(run.period_end)}
              </p>
              <p className="text-[12.5px] text-muted-foreground truncate">
                {run.paid_at
                  ? `Paid ${formatDate(run.paid_at)}`
                  : run.distributed_at
                    ? `Distributed ${formatDate(run.distributed_at)}`
                    : run.finalized_at
                      ? `Finalized ${formatDate(run.finalized_at)}`
                      : 'Open draft'}
                {payslip.delivered_at ? ` · this payslip emailed ${formatDate(payslip.delivered_at)}` : ''}
              </p>
            </div>
            <Link
              to={`/dashboard/payroll/runs/${run.id}`}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground hover:text-saffron-foreground transition-colors"
            >
              View run <ExternalLink className="h-3 w-3" />
            </Link>
          </section>
        </div>
      </div>

      {/* PDF preview side panel */}
      <aside className="w-[400px] 2xl:w-[480px] border-l border-border/40 bg-card/60 hidden xl:flex flex-col">
        <PayslipPdfPreview
          payslipId={payslip.id}
          label={`${fullName} · ${formatDate(run.period_start)}`}
        />
      </aside>
    </div>
  );
}

function ComponentsTable({
  rows, currency, sign,
}: { rows: PayrollComponent[]; currency: string; sign: '+' | '−' }) {
  const subtotal = rows.reduce((s, c) => s + Number(c.amount) * Number(c.occurrences), 0);
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground/80">
            <th className="text-left py-2.5 px-2 font-semibold">Type</th>
            <th className="text-left py-2.5 px-2 font-semibold">Description</th>
            <th className="text-right py-2.5 px-2 font-semibold">Qty</th>
            <th className="text-right py-2.5 px-2 font-semibold">Amount</th>
            <th className="text-right py-2.5 px-2 font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const total = Number(c.amount) * Number(c.occurrences);
            return (
              <tr key={c.id} className="border-t border-border/40">
                <td className="py-3 px-2 align-top">
                  <span className="text-[11.5px] font-mono uppercase tracking-wide text-foreground/85">{c.component_type}</span>
                  {c.source === 'manual' && (
                    <Badge variant="secondary" className="ml-1.5 text-[10px] uppercase tracking-wide">manual</Badge>
                  )}
                </td>
                <td className="py-3 px-2 align-top text-[13.5px] text-foreground">{c.description}</td>
                <td className="py-3 px-2 align-top text-right text-[13px] font-mono tabular-nums">{c.occurrences}</td>
                <td className="py-3 px-2 align-top text-right text-[13px] font-mono tabular-nums">{formatMoney(c.amount, currency)}</td>
                <td className="py-3 px-2 align-top text-right text-[14px] font-semibold font-mono tabular-nums">
                  {sign}{formatMoney(total, currency)}
                </td>
              </tr>
            );
          })}
          <tr className="border-t border-border/40">
            <td colSpan={4} className="py-3 px-2 text-right text-[12px] uppercase tracking-[0.14em] text-muted-foreground/80 font-semibold">
              Subtotal
            </td>
            <td className="py-3 px-2 text-right text-[14px] font-semibold font-mono tabular-nums text-foreground">
              {sign}{formatMoney(subtotal, currency)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function YtdStat({ label, value, emphasized = false }: { label: string; value: React.ReactNode; emphasized?: boolean }) {
  return (
    <div className={[
      'rounded-2xl ring-1 px-5 py-4',
      emphasized ? 'bg-saffron/10 ring-saffron/25' : 'bg-muted/40 ring-border/40',
    ].join(' ')}>
      <p className={[VISUAL.eyebrow, 'mb-1'].join(' ')}>{label}</p>
      <p className="font-sans font-semibold text-[20px] tabular-nums tracking-[-0.02em] text-foreground">{value}</p>
    </div>
  );
}

function SumRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

function MiniKpi({ label, value, tone }: { label: string; value: React.ReactNode; tone: 'success' | 'muted' }) {
  return (
    <div className={[
      'rounded-2xl ring-1 px-5 py-4',
      tone === 'success' ? 'bg-success/10 ring-success/25' : 'bg-muted/40 ring-border/40',
    ].join(' ')}>
      <p className={[VISUAL.eyebrow, 'mb-1'].join(' ')}>{label}</p>
      <p className="font-sans font-semibold text-[20px] tabular-nums tracking-[-0.02em] text-foreground">{value}</p>
    </div>
  );
}

// Labels reflect what the data actually tracks: `delivered_at` records the
// payslip EMAIL, not a bank payment — so "Emailed" / "Not sent", never "Paid".
function payslipStatus(slip: Payslip, run: PayrollRun): { tone: StatusTone; label: string } {
  if (run.status === 'cancelled') return { tone: 'muted', label: 'Cancelled' };
  // A paid run means the money actually moved — that outranks the email state.
  if (run.status === 'paid') return { tone: 'paid', label: 'Paid' };
  if (slip.delivered_at) return { tone: 'success', label: 'Emailed' };
  if (run.distributed_at) return { tone: 'warning', label: 'Not sent' }; // run distributed but this slip's email didn't go out
  if (run.status === 'finalized') return { tone: 'info', label: 'Pending' };
  return { tone: 'warning', label: 'Draft' };
}
